import { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { RichDoc } from "@edm/schema";
import { RICHTEXT_ALLOWED_TAGS } from "@edm/schema";
import { MergeTag } from "./mergeTagExtension";
import { normalizeTiptapJson, richDocToTiptap } from "./normalize";
import { useEditorStore } from "../store/editorStore";
import { fetchMergeTags, type MergeTagOption } from "../mergeTags";
import { fetchCapabilities } from "../../api/capabilities";
import { AiProposalDialog, type AssistRequest } from "../ai/AiProposalDialog";
import { richTextFromPlain, richTextToPlain } from "@edm/schema";

const COMMIT_DEBOUNCE_MS = 400;

/** Strip pasted HTML down to the shared rich-text allowlist before Tiptap
 * parses it. Server-side sanitization remains the authority at render time. */
function sanitizePastedHtml(html: string): string {
    const allowed = new Set<string>(RICHTEXT_ALLOWED_TAGS);
    const doc = new DOMParser().parseFromString(html, "text/html");
    const walk = (el: Element) => {
        for (const child of [...el.children]) {
            walk(child);
            const tag = child.tagName.toLowerCase();
            if (!allowed.has(tag)) {
                // unwrap: keep text/children, drop the element
                child.replaceWith(...child.childNodes);
                continue;
            }
            for (const attr of [...child.attributes]) {
                const keep =
                    (tag === "a" && attr.name === "href") ||
                    attr.name === "data-merge-tag";
                if (!keep) child.removeAttribute(attr.name);
            }
            if (tag === "a") {
                const href = child.getAttribute("href") ?? "";
                if (/^\s*(javascript|data|vbscript):/i.test(href)) {
                    child.removeAttribute("href");
                }
            }
        }
    };
    walk(doc.body);
    return doc.body.innerHTML;
}

interface Props {
    nodeId: string;
    value: RichDoc;
    /** Single-line mode for headings (no lists, Enter commits nothing). */
    singleParagraph?: boolean;
    onCommit: (content: RichDoc) => void;
}

export function RichTextEditor({ nodeId, value, singleParagraph, onCommit }: Props) {
    const externalEpoch = useEditorStore((s) => s.externalEpoch);
    const lastCommittedRef = useRef<string>(JSON.stringify(value));
    const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [focused, setFocused] = useState(false);
    // AI dialog state lives here (not in the toolbar) so it survives the blur
    // that opening the modal causes.
    const [aiRequest, setAiRequest] = useState<AssistRequest | null>(null);

    const extensions = useMemo(
        () => [
            StarterKit.configure({
                undoRedo: false,
                heading: false,
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                ...(singleParagraph ? { bulletList: false, orderedList: false, listItem: false } : {}),
                link: { openOnClick: false },
            }),
            MergeTag,
        ],
        [singleParagraph],
    );

    const editor = useEditor({
        extensions,
        content: richDocToTiptap(value),
        editorProps: {
            transformPastedHTML: sanitizePastedHtml,
            attributes: { class: "rte-content" },
        },
        onFocus: () => setFocused(true),
        onBlur: ({ editor }) => {
            setFocused(false);
            flushCommit(editor);
        },
        onUpdate: ({ editor }) => {
            if (commitTimer.current) clearTimeout(commitTimer.current);
            commitTimer.current = setTimeout(() => flushCommit(editor), COMMIT_DEBOUNCE_MS);
        },
    });

    const flushCommit = (ed: Editor) => {
        if (commitTimer.current) {
            clearTimeout(commitTimer.current);
            commitTimer.current = null;
        }
        const normalized = normalizeTiptapJson(ed.getJSON());
        const serialized = JSON.stringify(normalized);
        if (serialized === lastCommittedRef.current) return;
        lastCommittedRef.current = serialized;
        onCommit(normalized);
    };

    // External document changes (undo/redo/restore/conflict-reload): reload
    // content unless it matches what we last committed.
    useEffect(() => {
        if (!editor) return;
        const incoming = JSON.stringify(value);
        if (incoming !== lastCommittedRef.current) {
            lastCommittedRef.current = incoming;
            editor.commands.setContent(richDocToTiptap(value), { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalEpoch, editor]);

    useEffect(() => () => {
        if (commitTimer.current) clearTimeout(commitTimer.current);
    }, []);

    if (!editor) return null;

    return (
        <div className={`rte-wrap${focused ? " focused" : ""}`} data-node-id={nodeId}>
            {focused && (
                <Toolbar
                    editor={editor}
                    singleParagraph={singleParagraph}
                    onRequestAi={setAiRequest}
                />
            )}
            <EditorContent editor={editor} />
            {aiRequest && (
                <AiProposalDialog
                    request={aiRequest}
                    onClose={() => setAiRequest(null)}
                    onAccept={(text) => {
                        const next = richTextFromPlain(text);
                        lastCommittedRef.current = JSON.stringify(next);
                        editor.commands.setContent(richDocToTiptap(next), { emitUpdate: false });
                        onCommit(next);
                    }}
                />
            )}
        </div>
    );
}

function Toolbar({
    editor,
    singleParagraph,
    onRequestAi,
}: {
    editor: Editor;
    singleParagraph?: boolean;
    onRequestAi: (request: AssistRequest) => void;
}) {
    const [showTags, setShowTags] = useState(false);
    const [showAi, setShowAi] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);
    const [tags, setTags] = useState<MergeTagOption[]>([]);
    useEffect(() => {
        void fetchMergeTags().then(setTags);
        void fetchCapabilities().then((c) => setAiEnabled(c.ai));
    }, []);

    const AI_ACTIONS: { action: string; label: string; tone?: string }[] = [
        { action: "rewrite", label: "Rewrite" },
        { action: "shorten", label: "Shorten" },
        { action: "expand", label: "Expand" },
        { action: "simplify", label: "Simplify" },
        { action: "fix-grammar", label: "Fix grammar" },
        { action: "change-tone", label: "Friendlier tone", tone: "friendly" },
    ];

    const startAi = (action: string, tone?: string) => {
        const doc = normalizeTiptapJson(editor.getJSON());
        const text = richTextToPlain(doc);
        onRequestAi({ action, text, tone });
        setShowAi(false);
    };
    const btn = (
        label: string,
        active: boolean,
        onClick: () => void,
        title: string,
    ) => (
        <button
            type="button"
            className={`rte-btn${active ? " active" : ""}`}
            title={title}
            aria-label={title}
            aria-pressed={active}
            onMouseDown={(e) => {
                e.preventDefault();
                onClick();
            }}
        >
            {label}
        </button>
    );

    return (
        <div className="rte-toolbar" role="toolbar" aria-label="Text formatting">
            {btn("B", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "Bold")}
            {btn("I", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "Italic")}
            {btn("U", editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "Underline")}
            {btn("S", editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), "Strikethrough")}
            {btn("🔗", editor.isActive("link"), () => {
                const prev = editor.getAttributes("link").href as string | undefined;
                const href = window.prompt("Link URL", prev ?? "https://");
                if (href === null) return;
                if (href === "") editor.chain().focus().unsetLink().run();
                else editor.chain().focus().setLink({ href }).run();
            }, "Link")}
            {!singleParagraph &&
                btn("• List", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "Bullet list")}
            {aiEnabled && (
                <span className="rte-tags">
                    {btn("✨", showAi, () => setShowAi((v) => !v), "AI assist")}
                    {showAi && (
                        <span className="rte-tag-menu" role="menu">
                            {AI_ACTIONS.map((a) => (
                                <button
                                    key={a.action}
                                    type="button"
                                    role="menuitem"
                                    className="rte-tag-item"
                                    data-testid={`ai-action-${a.action}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        startAi(a.action, a.tone);
                                    }}
                                >
                                    {a.label}
                                </button>
                            ))}
                        </span>
                    )}
                </span>
            )}
            <span className="rte-tags">
                {btn("{{tag}}", showTags, () => setShowTags((v) => !v), "Insert merge tag")}
                {showTags && (
                    <span className="rte-tag-menu" role="menu">
                        {tags.map((t) => (
                            <button
                                key={t.tag}
                                type="button"
                                role="menuitem"
                                className="rte-tag-item"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    editor.chain().focus().insertMergeTag(t.tag, t.fallback).run();
                                    setShowTags(false);
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </span>
                )}
            </span>
        </div>
    );
}
